import { useQuery } from "@tanstack/react-query";
import { cn } from "@wags/ui/ui-utils";
import { Fragment, lazy, type ReactNode, Suspense, use } from "react";
import type { FindFeedRowKey } from "../routes/browse-route-helpers";
import { FeedSkeleton } from "./FeedSkeleton";
import { streamedFeedRows } from "./feedRows";
import type { FindFeedData } from "./findFeedData";
import { ListingRow } from "./ListingRow";
import { SECTION_CONTENT_CLASS } from "./listingRowLayout";
import { ReactTreeWhenNear } from "./react-tree-when-near";

const CinematicVideoBand = lazy(() =>
    import("./warmth/VideoBands").then((mod) => ({
        default: mod.CinematicVideoBand,
    })),
);

type FeedRow = FindFeedData["nonEmptyRows"][number];

const BROWSE_BAND_AFTER_ROW_INDEX = 2;
const VIDEO_BAND_AFTER_ROW_INDEX = 5;
const VIDEO_BAND_MIN_ROWS = 7;
const FIRST_SCREEN_ROW_COUNT = 1;

export function FeedRowList({
    rows,
    feedQuery,
    feedReadyPromise,
    browseBand,
    firstRowAccessory,
    onRowClick,
}: {
    rows: FeedRow[];
    feedQuery: FindFeedData["findFeedQuery"];
    feedReadyPromise: Promise<void> | undefined;
    browseBand: ReactNode;
    firstRowAccessory?: ReactNode;
    onRowClick: (rowKey: FindFeedRowKey) => void;
}) {
    const { data: streamedFeed } = useQuery({ ...feedQuery, enabled: false });
    const streamedRows = streamedFeedRows(streamedFeed);
    const renderRow = (row: FeedRow, index: number) => {
        const rowNode = (
            <div className={SECTION_CONTENT_CLASS}>
                <ListingRow
                    title={row.title}
                    dogListings={row.dogListings}
                    onClick={() => onRowClick(row.key)}
                    headerAccessory={index === 0 ? firstRowAccessory : null}
                />
            </div>
        );
        if (index < FIRST_SCREEN_ROW_COUNT) {
            return <Fragment key={row.key}>{rowNode}</Fragment>;
        }
        return (
            <ReactTreeWhenNear key={row.key} keepServerMarkup>
                {rowNode}
            </ReactTreeWhenNear>
        );
    };

    if (rows.length === 0) {
        if (!streamedRows && feedReadyPromise) use(feedReadyPromise);
        if (!streamedRows?.length) return <FeedSkeleton />;
        return streamedRows.map(renderRow);
    }

    const out: ReactNode[] = [];
    rows.forEach((row, i) => {
        out.push(renderRow(row, i));
        if (i === Math.min(BROWSE_BAND_AFTER_ROW_INDEX, rows.length - 1)) {
            out.push(
                <ReactTreeWhenNear key="browse-zone" keepServerMarkup>
                    {browseBand}
                </ReactTreeWhenNear>,
            );
        }
        if (
            rows.length >= VIDEO_BAND_MIN_ROWS &&
            i === VIDEO_BAND_AFTER_ROW_INDEX
        ) {
            out.push(
                <ReactTreeWhenNear key="video-band">
                    <div className={cn(SECTION_CONTENT_CLASS, "py-4 md:py-6")}>
                        <Suspense fallback={null}>
                            <CinematicVideoBand />
                        </Suspense>
                    </div>
                </ReactTreeWhenNear>,
            );
        }
    });
    return out;
}
